import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import router from '@/routes';

import ApiService from '../services/ApiService';
import keycloak from '../services/keycloak';

import { useAuthCommonStore } from './authCommonStore';
import { LoginRequest, AuthUser, RefreshLoginRequest, LogoutRequest } from '../models/AuthModels';
import { useJwtInfo } from '@/modules/auth/composables/useJwtInfo';

export const useAuthStore: any = defineStore('authStore', () => {
  const commonStore = useAuthCommonStore();

  const kc = keycloak;
  const authUser = ref(
    sessionStorage.getItem('authUser') != null && sessionStorage.getItem('authUser') !== undefined ? JSON.parse(sessionStorage.authUser) : null
  );

  const token = ref<string>('');
  const refreshToken = ref<string>('');

  const username = ref<string>('');
  const name = ref<string>('');
  const email = ref<string>('');

  const roles = ref<string[]>([]);

  const initialized = ref<boolean>(false);
  let initializationPromise: Promise<void> | null = null;

  // const isAuthenticated = computed(() => kc.authenticated);
  const isAuthenticated = computed(() => {
    if (kc.authenticated) {
      return true;
    } else if (authUser.value != null && authUser.value.lagerToken != null) {
      return true;
    } else {
      return false;
    }
  });

  const isAuthorized = computed(() => roles.value.includes('access-ewerp'));
  const isEwerpAdmin = computed(() => kc.authenticated === true && roles.value.includes('ewerp-admin'));

  function resetAuthState() {
    authUser.value = null;

    token.value = '';
    refreshToken.value = '';

    username.value = '';
    name.value = '';
    email.value = '';
    roles.value = [];
  }

  async function logout() {
    if (kc.authenticated) {
      keycloakLogout();
    } else if (authUser.value != null) {
      erpLogout();
    } else {
      resetAuthState();
      sessionStorage.clear();
      router.push({ name: 'auth.login' });
    }
  }

  function isTokenExpired(requestUrl: string, minValidity: number = 60): boolean {
    if (kc.authenticated && kc.token && kc.isTokenExpired(minValidity)) {
      return true;
    } else if (authUser.value != null && authUser.value.lagerToken != null) {
      const { secondsLeft } = useJwtInfo(authUser.value.lagerToken);
      if (secondsLeft.value !== null && secondsLeft.value <= minValidity && !requestUrl.includes('/login/refresh')) {
        return true;
      }
    }

    return false;
  }

  async function refreshLogin() {
    if (kc.authenticated) {
      await keycloakUpdateToken(60);
    } else if (authUser.value != null && authUser.value.lagerToken != null) {
      await erpRefreshLogin();
    } else {
      sessionStorage.clear();
      router.push({ name: 'auth.login' });
    }
  }

  async function initAuth() {
    if (initialized.value) return;

    if (initializationPromise !== null) {
      return initializationPromise;
    }

    initializationPromise = initializeAuth();

    try {
      await initializationPromise;
    } finally {
      initializationPromise = null;
    }
  }

  async function initializeAuth() {
    if (sessionStorage.getItem('authUser') != null && sessionStorage.getItem('authUser') !== undefined) {
      const user: AuthUser = JSON.parse(sessionStorage.authUser);
      setAuthStatusFromAuthUser(user);
      initialized.value = true;
      return;
    }

    try {
      const authenticated = await kc.init({
        onLoad: 'check-sso', // Prüft nur SSO-Status, leitet nicht automatisch weiter
        checkLoginIframe: false, // Optional, um die iframe-Überprüfung zu deaktivieren
        // silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        // enableLogging: true, // Optional, um Protokollierung zu aktivieren
        pkceMethod: 'S256', // Optional, um PKCE zu verwenden
      });

      // sobald init durch ist, Fragment aus der URL löschen
      if (window.location.hash.includes('code=')) {
        history.replaceState(history.state, document.title, window.location.pathname + window.location.search);
      }

      // console.log('initAuth → authenticated:', authenticated, 'kc.authenticated:', kc.authenticated, 'token length:', kc.token?.length);
      initialized.value = true;
      if (authenticated && kc.token) {
        // console.log(kc);
        token.value = kc.token;
        refreshToken.value = kc.refreshToken || '';
        roles.value = kc.tokenParsed?.realm_access?.roles || [];
        username.value = kc.idTokenParsed?.preferred_username || '';
        name.value = kc.idTokenParsed?.name || '';
        email.value = kc.idTokenParsed?.email || '';

        // Nach erfolgreichem SSO-Login zur Bestellungen-Seite weiterleiten, wenn man derzeit auf der Login-Seite ist
        if (router.currentRoute.value.name === 'auth.login') {
          router.push({ name: 'ewwms.bestellungen' });
        }
      }
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
    }
  }

  //#region Keycloak
  function keycloakLogin() {
    sessionStorage.clear(); // Session-Storage leeren, um alte Daten zu entfernen

    // const redirectUri = window.location.origin + router.resolve({ name: 'ewwms.bestellungen' }).href;
    const redirectUri = window.location.origin;
    // kc ist bereits durch initAuth() initialisiert, daher können wir direkt login() aufrufen
    kc.login({ redirectUri: redirectUri });
  }

  function keycloakLogout() {
    sessionStorage.clear(); // Session-Storage leeren, um alte Daten zu entfernen
    // Keycloak kümmert sich um Redirect und Session-Cleanup

    // redirectUri soll die Login-Seite sein
    const redirectUri = window.location.origin + router.resolve({ name: 'auth.login' }).href;
    kc.logout({ redirectUri: redirectUri });
  }

  async function keycloakUpdateToken(minValidity: number = 60) {
    const refreshed = await kc.updateToken(minValidity);
    if (refreshed && kc.token) {
      token.value = kc.token!;
    }

    return refreshed;
  }
  //#endregion

  //#region EW ERP API
  function setAuthStatusFromAuthUser(user: AuthUser) {
    authUser.value = user;

    token.value = user.lagerToken;
    refreshToken.value = user.refreshToken;
    username.value = user.lagerUser.username;
    name.value = user.lagerUser.mitarbeitername;
    // email.value = user.lagerUser.email;
    const tokenInfo = useJwtInfo(user.lagerToken);
    const rolesFromToken = tokenInfo.parsedToken.value?.realm_access?.roles;

    roles.value = rolesFromToken || [];

    sessionStorage.setItem('authUser', JSON.stringify(user));
  }

  async function erpLogin(data: LoginRequest) {
    commonStore.setStatus({ ...commonStore.status, authLoading: true, authErrorMsg: '' });

    return ApiService.login(data)
      .then((res) => {
        const user: AuthUser = res.data;

        // Parameter setzen
        setAuthStatusFromAuthUser(user);

        router.push({ name: 'ewwms.bestellungen' });
      })
      .catch((error) => {
        commonStore.status.authErrorMsg = error.response.data.title;
        sessionStorage.removeItem('authUser');
      })
      .finally(() => {
        commonStore.setStatus({ ...commonStore.status, authLoading: false });
      });
  }

  async function erpRefreshLogin() {
    const request = new RefreshLoginRequest(refreshToken.value);
    const res = await ApiService.refreshLogin(request);

    const user: AuthUser = res.data;

    // Parameter setzen
    setAuthStatusFromAuthUser(user);

    return;
  }

  async function erpLogout() {
    commonStore.setStatus({ ...commonStore.status, authLoading: true, authErrorMsg: '' });

    return ApiService.logout(new LogoutRequest(refreshToken.value))
      .then(() => {
        console.log('LOGOUT');
      })
      .catch(() => {
        console.log('LOGOUT CATCH');
      })
      .finally(async () => {
        commonStore.status.authLoading = false;
        resetAuthState();
        sessionStorage.clear();

        let lagerortid = localStorage.getItem('lagerortid') != null ? JSON.parse(localStorage.getItem('lagerortid')!) : '102';

        localStorage.clear();

        localStorage.setItem('lagerortid', lagerortid);
        router.push({ name: 'auth.login' });
      });
  }
  //#endregion

  return {
    kc,
    token,
    username,
    name,
    email,
    roles,

    isAuthenticated,
    isAuthorized,
    isEwerpAdmin,

    logout,
    refreshLogin,
    isTokenExpired,

    //#region Keycloak
    initAuth,
    keycloakLogin,
    keycloakLogout,
    keycloakUpdateToken,
    //#endregion

    //#region EW ERP API
    erpLogin,
    erpRefreshLogin,
    erpLogout,
    //#endregion
  };
});
